import React, { useState, useEffect } from 'react';
import { Paper, Box, Typography, Avatar, Chip, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import supabase from '../../../supabaseClient';
import { getInitials, getAvatarColor } from './attendanceDisplayUtils';

// Ustala status pracownika na wybrany dzień na podstawie wpisów obecności / urlopów
function deriveEmployeeStatus(employeeId, attendance, absences) {
    const entries = attendance.filter(a => a.employee_id === employeeId);

    if (entries.length > 0) {
        const inProgress = entries.find(e => e.check_out === null);
        if (inProgress) {
            const start = new Date(inProgress.check_in);
            return {
                status: 'working',
                caption: `od ${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
            };
        }

        const totalMinutes = entries.reduce((sum, e) => {
            const start = new Date(e.check_in);
            const end = new Date(e.check_out);
            return sum + Math.max(0, Math.round((end - start) / 60000));
        }, 0);
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        return { status: 'done', caption: `${h}h ${m}m` };
    }

    const onLeave = absences.some(ab => ab.employee_id === employeeId);
    if (onLeave) return { status: 'absence', caption: 'urlop' };

    return { status: 'absent', caption: 'nieobecny' };
}

function AttendanceRosterComponent({ idConfig, dateString, attendance, absences, selectedEmployeeId, onSelectEmployee }) {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!idConfig) return;
        const fetchEmployees = async () => {
            setLoading(true);
            const { data, error } = await supabase
                .from('employees')
                .select('id, name, surname, archived')
                .eq('id_configuration', idConfig)
                .is('archived', null)
                .order('name');
            if (error) {
                console.log('Błąd pobierania załogi:', error);
            } else {
                setEmployees(data || []);
            }
            setLoading(false);
        };
        fetchEmployees();
    }, [idConfig]);

    if (loading) {
        return (
            <Paper sx={{ p: 3, mt: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
            </Paper>
        );
    }

    if (employees.length === 0) return null;

    const roster = employees.map(emp => ({
        ...emp,
        ...deriveEmployeeStatus(emp.id, attendance, absences),
    }));

    const workingCount = roster.filter(e => e.status === 'working').length;
    const doneCount = roster.filter(e => e.status === 'done').length;
    const absentCount = roster.filter(e => e.status === 'absence' || e.status === 'absent').length;

    const selectedEmployee = roster.find(e => e.id === selectedEmployeeId);

    return (
        <Paper sx={{ p: 2.5, mt: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Załoga · {dateString}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', display: 'inline-block' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        w pracy <strong>{workingCount}</strong>
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.secondary', display: 'inline-block' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        był dzisiaj <strong>{doneCount}</strong>
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.400', display: 'inline-block' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        nieobecni <strong>{absentCount}</strong>
                    </Typography>
                </Box>

                {selectedEmployee && (
                    <Chip
                        sx={{ ml: 'auto' }}
                        label={`filtr: ${selectedEmployee.name} ${selectedEmployee.surname}`}
                        onDelete={() => onSelectEmployee(null)}
                        deleteIcon={<CloseIcon />}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                {roster.map(emp => {
                    const isAbsent = emp.status === 'absence' || emp.status === 'absent';
                    const isSelected = emp.id === selectedEmployeeId;
                    return (
                        <Box
                            key={emp.id}
                            onClick={() => onSelectEmployee && onSelectEmployee(isSelected ? null : emp.id)}
                            sx={{
                                width: 84,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                cursor: onSelectEmployee ? 'pointer' : 'default',
                                p: 0.75,
                                borderRadius: 2,
                                border: '2px solid',
                                borderColor: isSelected ? 'primary.main' : 'transparent',
                                bgcolor: isSelected ? 'rgba(107,29,47,0.06)' : 'transparent',
                                '&:hover': onSelectEmployee ? { bgcolor: 'rgba(0,0,0,0.03)' } : undefined,
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: isAbsent ? 'grey.300' : getAvatarColor(emp.name),
                                    color: isAbsent ? 'grey.600' : 'white',
                                    width: 48,
                                    height: 48,
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                }}
                            >
                                {getInitials(emp.name, emp.surname)}
                            </Avatar>
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    mt: 0.75,
                                    maxWidth: '100%',
                                    fontWeight: 500,
                                    color: isAbsent ? 'text.disabled' : 'text.primary',
                                }}
                            >
                                {emp.name} {emp.surname}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: emp.status === 'working' ? 'success.main' : 'text.secondary',
                                    fontWeight: emp.status === 'working' ? 600 : 400,
                                }}
                            >
                                {emp.caption}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}

export default AttendanceRosterComponent;
